import * as React from 'react';
import noop from 'lodash/noop';
import {useTheme, useSize, emotionStyled} from '@packages/utilities/styled';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {
  ColorSwatchPanel,
  enhanceInput,
  Swatch,
} from '@packages/systems/design-system/WebflowColorPicker';
import TextInput from '@packages/systems/design-system/TextInput';
import {View} from '@packages/systems/spring';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import {panelOpened} from '@packages/systems/panels';
import {
  cssVariablesToSwatches,
  VARIABLES_PANEL,
} from '@packages/systems/core/utils/StyleVariables';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const widthStyle = {width: rightSidebarWidth} as const;
const returnNull = () => null;

/**
 * We are editing a component that is using the legacy `styled` function.
 * We need to use `!important` to override the inline styles it sets.
 */
const StyledSwatch = emotionStyled(Swatch)({
  // @ts-expect-error - TS2322 - Type '"absolute !important"' is not assignable to type 'Position | undefined'.
  position: 'absolute !important',
  top: '0 !important',
  left: '0 !important',
  borderColor: '$uiBorderColor !important',
});

const disallowTextDragging = (e: React.MouseEvent<HTMLInputElement>) =>
  e.preventDefault();

const EnhancedColorInput = enhanceInput(
  // @ts-expect-error - TS2345 - Argument of type '({ focus, invalid, value, placeholder, style, name, disabled, onBlur, onChange, onFocus, onKeyDown, "data-automation-id": dataAutomationId, "aria-label": ariaLabel, }: { focus: any; invalid: any; value: any; placeholder: any; style: any; name: any; disabled: any; onBlur: any; onChange: any; onFocus: any; onKeyDown: ...' is not assignable to parameter of type '(props: unknown) => any'.
  ({
    focus,
    invalid,
    value,
    placeholder,
    style,
    name,
    disabled,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    'data-automation-id': dataAutomationId,
    'aria-label': ariaLabel,
  }) => {
    const {control, gutters} = useTheme();
    return (
      <TextInput
        data-automation-id={dataAutomationId}
        aria-label={ariaLabel}
        style={{
          paddingLeft: control.height + gutters.base,
          ...style,
        }}
        name={name}
        focus={focus}
        invalid={invalid}
        value={value}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDragStart={disallowTextDragging}
        onDrop={disallowTextDragging}
        disabled={disabled}
      />
    );
  }
);

type ColorInputProps = {
  value: string;
  onChange: (arg1: string) => void;
  dispatch: (arg1?: any) => void;
  'data-automation-id'?: string;
};

const selectCssVariableStore = (state: any) => state.CssVariablesStore;

export function ColorPickerInput({
  onChange,
  value,
  dispatch,
  'data-automation-id': dataAutomationId,
}: ColorInputProps) {
  const size = useSize();
  const cssVariablesState = useDesignerSelector(selectCssVariableStore);

  const swatches = cssVariablesToSwatches(cssVariablesState.variables);

  const colorValue = React.useMemo(() => ({type: 'color', value}), [value]);

  const onColorPanelValueChange = React.useCallback(
    // @ts-expect-error - TS7031 - Binding element 'nextValue' implicitly has an 'any' type.
    ({value: nextValue}) => {
      if (nextValue.type === 'color') {
        onChange(nextValue.value);
      }

      if (nextValue.type === 'global-swatch-reference') {
        for (const {id, color} of swatches) {
          if (nextValue.id === id) {
            onChange(color);
            break;
          }
        }
      }
    },
    [swatches, onChange]
  );

  const openVariablePanel = () => {
    dispatch(panelOpened(VARIABLES_PANEL));
  };

  /**
   * This approach is a bit of a middle-ground hack to preserve existing structure as much as possible, while enabling future size mode changes.
   *
   * We are now memoizing `Swatch` (instead of relying on `react-compose` HOC).
   * This means that the component's memoization is much less brittle and indirect.
   * However, doing so causes a Type error with legacy `styled` function.
   * So, we are migrating `StyledSwatch` to use `emotionStyled` instead.
   *
   * The `StyledSwatch` relied on incoming legacy theme values for its sizing (in Editor & Designer).
   * We can't use `emotionStyled` variants to replicate this because the `Swatch` component is using its incoming `style` prop to set both its own size and that of internal elements (its indicator icon).
   *
   * We do not want to use legacy `tokens.control` values here, since we are actively working to remove them.
   * By using Spring's size context, we can more easily support any upcoming size mode changes (e.g. Campaigns mode is currently in development; it does not rely on `Editor`/`Designer` theme toggling).
   * Yet, we do not have semantic values for these within Spring's size mode context. So, we are using manual values here.
   */
  const swatchStyles = React.useMemo(
    () => ({
      width: size === 'medium' ? 24 : 32,
      height: size === 'medium' ? 24 : 32,
      borderTopLeftRadius: size === 'medium' ? 4 : 3,
      borderBottomLeftRadius: size === 'medium' ? 4 : 3,
    }),
    [size]
  );

  return (
    <View position="relative" width="100%">
      <DeprecatedPopoverBridge
        metricsIdentifier="color-picker-input-popover"
        renderPopoverContent={() => (
          <div style={widthStyle}>
            <ColorSwatchPanel
              getNativeElement={returnNull}
              roleCanEditSwatches={false}
              showColorContrastLines={false}
              toggleColorContrastLines={noop}
              getTooltipContentDisabled={() => ''}
              editAccess={false}
              // @ts-expect-error - TS2769 - No overload matches this call.
              value={colorValue}
              swatches={swatches}
              onChange={onColorPanelValueChange}
              openVariablePanel={openVariablePanel}
            />
          </div>
        )}
        showArrow={false}
        showMask="TRANSPARENT"
      >
        {({openPopover, Anchor}) => (
          <Anchor asChild>
            <StyledSwatch
              onClick={openPopover}
              color={value || 'transparent'}
              style={swatchStyles}
            />
          </Anchor>
        )}
      </DeprecatedPopoverBridge>

      <EnhancedColorInput
        allowEmpty
        onColorChange={onChange}
        value={value}
        placeholder="Enter a color..."
        data-automation-id={dataAutomationId}
      />
    </View>
  );
}
